import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, X, Search } from 'lucide-react';
import { FilterChipProps } from './types';
import { DateRangePicker } from '../DateRangePicker';
export const FilterChip: React.FC<FilterChipProps> = ({
  filter,
  onSelect,
  onRemove,
  onCustomDateChange,
  'data-id': dataId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [searchValue, setSearchValue] = useState(filter.selectedValue || '');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowDatePicker(false);
      }
    };
    if (isOpen || showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, showDatePicker]);
  // Focus input when opened for search filters
  useEffect(() => {
    if (isOpen && filter.type === 'search' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, filter.type]);
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(!isOpen);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      setShowDatePicker(false);
    }
  };
  const handleSearchSubmit = () => {
    onSelect(searchValue);
    setIsOpen(false);
  };
  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };
  const handleOptionSelect = (value: string) => {
    if (value === 'custom') {
      setShowDatePicker(true);
      setIsOpen(false);
    } else {
      if (filter.multiSelect) {
        // For multi-select, toggle the value in the array
        const currentValues = filter.selectedValues || [];
        const newValues = currentValues.includes(value) ? currentValues.filter(v => v !== value) : [...currentValues, value];
        onSelect(newValues);
        // Don't close dropdown for multi-select to allow multiple selections
      } else {
        onSelect(value);
        setIsOpen(false);
        setShowDatePicker(false);
      }
    }
  };
  const handleDateRangeChange = (startDate: string, endDate: string) => {
    if (onCustomDateChange) {
      onCustomDateChange(filter.id, startDate, endDate);
    }
    onSelect('custom');
    setShowDatePicker(false);
  };
  const selectedOption = filter.options.find(opt => opt.value === filter.selectedValue);
  // Get selected options for multi-select filters
  const selectedOptions = filter.multiSelect ? filter.options.filter(opt => filter.selectedValues?.includes(opt.value)) : [];
  // Format display label for custom date range
  const getDisplayLabel = () => {
    // For search-type filters, show the search value
    if (filter.type === 'search') {
      if (filter.selectedValue && filter.selectedValue.trim()) {
        return `${filter.label}: "${filter.selectedValue}"`;
      }
      return filter.label;
    }
    // For multi-select filters, show the selected values
    if (filter.multiSelect && selectedOptions.length > 0) {
      if (selectedOptions.length === 1) {
        // Single selection - show "Label: Value"
        const option = selectedOptions[0];
        if (option.value === 'custom' && filter.customDateRange) {
          const start = new Date(filter.customDateRange.startDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
          const end = new Date(filter.customDateRange.endDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
          return `${filter.label}: ${start} - ${end}`;
        }
        return `${filter.label}: ${option.label}`;
      } else {
        // Multiple selections - show "Label: X selected"
        return `${filter.label}: ${selectedOptions.length} selected`;
      }
    }
    // For single-select filters
    if (filter.selectedValue === 'custom' && filter.customDateRange) {
      const start = new Date(filter.customDateRange.startDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      const end = new Date(filter.customDateRange.endDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      return `${filter.label}: ${start} - ${end}`;
    }
    return selectedOption ? `${filter.label}: ${selectedOption.label}` : filter.label;
  };
  const displayLabel = getDisplayLabel();
  return <div className="relative inline-block" ref={dropdownRef} data-id="element-1501">
      <button onClick={() => setIsOpen(!isOpen)} onKeyDown={handleKeyDown} className={`
          h-10 pl-4 pr-16 flex items-center gap-2 text-[14px] font-medium
          bg-[#E0F2FE] border border-[#0EA5E9] rounded-md transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20
          hover:bg-[#DBEAFE] text-[#0F172A]
        `} aria-haspopup="listbox" aria-expanded={isOpen} data-id="element-1502">
        <span className="whitespace-nowrap" data-id="element-1503">{displayLabel}</span>
        {filter.type === 'search' ? <Search size={16} className="absolute right-8 text-[#475569] pointer-events-none" data-id="element-1504" /> : <ChevronDownIcon size={16} className={`absolute right-8 text-[#475569] transition-transform duration-200 pointer-events-none ${isOpen ? 'rotate-180' : ''}`} data-id="element-1505" />}
        {onRemove && <button onClick={e => {
        e.stopPropagation();
        onRemove();
      }} className="absolute right-2 p-0.5 hover:bg-[#0EA5E9]/20 rounded transition-colors" aria-label="Remove filter" data-id="element-1506">
            <X size={14} className="text-[#475569]" data-id="element-1507" />
          </button>}
      </button>

      {/* Search input for search-type filters */}
      {isOpen && filter.type === 'search' && <div className="absolute top-full left-0 mt-2 min-w-[280px] bg-white border border-[#CBD5E1] rounded-lg shadow-lg z-[1001] p-4" data-id="element-1508">
          <h3 className="text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-1509">
            Search {filter.label}
          </h3>
          <div className="space-y-3" data-id="element-1510">
            <input ref={inputRef} type="text" value={searchValue} onChange={e => setSearchValue(e.target.value)} onKeyDown={handleSearchKeyDown} placeholder={`Enter ${filter.label.toLowerCase()}...`} className="w-full h-10 px-3 text-[14px] border border-[#CBD5E1] rounded-md focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20" data-id="element-1511" />
            <div className="flex gap-2" data-id="element-1512">
              <button type="button" onClick={() => {
            setSearchValue('');
            setIsOpen(false);
          }} className="flex-1 h-10 px-4 text-[14px] font-medium text-[#475569] bg-white border border-[#CBD5E1] rounded-md hover:bg-[#F1F5F9] transition-colors" data-id="element-1513">
                Cancel
              </button>
              <button type="button" onClick={handleSearchSubmit} className="flex-1 h-10 px-4 text-[14px] font-medium text-white bg-[#0EA5E9] rounded-md hover:bg-[#0284C7] transition-colors" data-id="element-1514">
                Apply
              </button>
            </div>
          </div>
        </div>}

      {/* Dropdown for regular filters */}
      {isOpen && !showDatePicker && filter.type !== 'search' && <div className="absolute top-full left-0 mt-2 min-w-full bg-white border border-[#CBD5E1] rounded-md shadow-lg z-[1001] overflow-hidden" data-id="element-1515">
          <ul role="listbox" className="py-1" data-id="element-1516">
            {filter.options.map(option => {
          const isSelected = filter.multiSelect ? filter.selectedValues?.includes(option.value) : option.value === filter.selectedValue;
          return <li key={option.id} data-id="element-1517">
                  <button onClick={() => handleOptionSelect(option.value)} className={`
                      w-full px-4 py-2.5 text-left text-[14px] transition-colors duration-200
                      hover:bg-[#F1F5F9] flex items-center gap-2
                      ${isSelected ? 'bg-[#E0F2FE] text-[#0EA5E9] font-medium' : 'text-[#475569]'}
                    `} role="option" aria-selected={isSelected} data-id="element-1518">
                    {filter.multiSelect && <div className={`w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-1519">
                        {isSelected && <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1520">
                            <path d="M5 13l4 4L19 7" data-id="element-1521"></path>
                          </svg>}
                      </div>}
                    {option.label}
                  </button>
                </li>;
        })}
          </ul>
        </div>}

      {showDatePicker && <div className="absolute top-full left-0 mt-2 bg-white border border-[#CBD5E1] rounded-lg shadow-lg z-[1001] p-4 min-w-[280px]" data-id="element-1522">
          <h3 className="text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-1523">
            Select Date Range
          </h3>
          <div className="space-y-3" data-id="element-1524">
            <div data-id="element-1525">
              <label className="block text-[12px] font-medium text-[#475569] mb-1" data-id="element-1526">
                Start Date
              </label>
              <input type="date" value={filter.customDateRange?.startDate || ''} onChange={e => {
            if (onCustomDateChange && filter.customDateRange) {
              onCustomDateChange(filter.id, e.target.value, filter.customDateRange.endDate);
            }
          }} max={filter.customDateRange?.endDate || undefined} className="w-full h-10 px-3 text-[14px] border border-[#CBD5E1] rounded-md focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20" data-id="element-1527" />
            </div>
            <div data-id="element-1528">
              <label className="block text-[12px] font-medium text-[#475569] mb-1" data-id="element-1529">
                End Date
              </label>
              <input type="date" value={filter.customDateRange?.endDate || ''} onChange={e => {
            if (onCustomDateChange && filter.customDateRange) {
              onCustomDateChange(filter.id, filter.customDateRange.startDate, e.target.value);
            }
          }} min={filter.customDateRange?.startDate || undefined} className="w-full h-10 px-3 text-[14px] border border-[#CBD5E1] rounded-md focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20" data-id="element-1530" />
            </div>
            <div className="flex gap-2 pt-2" data-id="element-1531">
              <button type="button" onClick={() => {
            setShowDatePicker(false);
            setIsOpen(false);
          }} className="flex-1 h-10 px-4 text-[14px] font-medium text-[#475569] bg-white border border-[#CBD5E1] rounded-md hover:bg-[#F1F5F9] transition-colors" data-id="element-1532">
                Cancel
              </button>
              <button type="button" onClick={() => {
            if (filter.customDateRange?.startDate && filter.customDateRange?.endDate) {
              handleDateRangeChange(filter.customDateRange.startDate, filter.customDateRange.endDate);
            }
          }} disabled={!filter.customDateRange?.startDate || !filter.customDateRange?.endDate} className="flex-1 h-10 px-4 text-[14px] font-medium text-white bg-[#0EA5E9] rounded-md hover:bg-[#0284C7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-1533">
                Apply
              </button>
            </div>
          </div>
        </div>}
    </div>;
};